import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { formatShortDate, formatViews } from "@/utils/article";

interface ArticleCardProps {
  article: Article;
}

export function ArticleCard({ article }: ArticleCardProps) {
  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex h-full cursor-pointer flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-all duration-300 hover:-translate-y-1 hover:border-primary-300"
    >
      <div className="aspect-[3/2] w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - ${article.categoryName}`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="mb-3 flex items-center gap-2 text-xs text-foreground-500">
          <span className="rounded-full bg-primary-100 px-2.5 py-0.5 font-medium text-primary-700">
            {article.categoryName}
          </span>
          <span>{formatShortDate(article.date)}</span>
        </div>
        <h3 className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-foreground-600">
          {article.summary}
        </p>
        <div className="mt-4 flex items-center justify-between text-xs text-foreground-400">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
          <span className="flex items-center gap-1 font-medium text-accent-600 transition-colors group-hover:gap-2">
            阅读全文
            <i className="ri-arrow-right-line" />
          </span>
        </div>
      </div>
    </Link>
  );
}

interface ArticleRowProps {
  article: Article;
}

export function ArticleRow({ article }: ArticleRowProps) {
  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex cursor-pointer gap-4 rounded-xl border border-background-200 bg-background-50 p-4 transition-all duration-300 hover:border-primary-300 hover:bg-background-100/60 md:gap-6 md:p-5"
    >
      <div className="h-24 w-32 shrink-0 overflow-hidden rounded-lg bg-background-200 sm:h-28 sm:w-44">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - ${article.categoryName}`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
          <span className="rounded-full bg-primary-100 px-2.5 py-0.5 font-medium text-primary-700">
            {article.categoryName}
          </span>
          <span>{formatShortDate(article.date)}</span>
          <span className="hidden items-center gap-1 sm:flex">
            <i className="ri-eye-line" />
            {formatViews(article.views)}
          </span>
        </div>
        <h3 className="line-clamp-2 font-heading text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
          {article.title}
        </h3>
        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-foreground-600">
          {article.summary}
        </p>
        <div className="mt-2 hidden flex-wrap gap-2 sm:flex">
          {article.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-900"
            >
              #{tag}
            </span>
          ))}
        </div>
      </div>
    </Link>
  );
}